import { EQuestionType, hasNestedKeys } from '@hubertai/screening-question-types';
import type { TSingleSelectOption } from '@hubertai/screening-question-types';
import { IconButton } from '@radix-ui/themes';
import { Trash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../context/create-question.context';
import UneditableInfoTooltip from '../../UneditableInfoTooltip';
import HubertInput from 'components/HubertUI/HubertInput';
import MutedText from 'components/common/MutedText';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import OptionActionPopover from 'components/Questionsv2/builder/OptionActionPopover';
import ReplyPopover from 'components/Questionsv2/builder/ReplyPopover';
import SelectOptionRolePopover from 'components/Questionsv2/builder/SelectOptionRolePopover';
import EvaluationPopover from 'components/Templates/builder/Evaluation/EvaluationPopover';
import type { EarlyReject } from 'components/Templates/builder/Evaluation/EvaluationPopover';
import { useTemplateId } from 'hooks/useTemplate';
import type { useQuestionEvaluation } from 'hooks/useQuestionEvaluation';
import type { TQuestionRule } from 'types/rules.types';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  addOption,
  removeOptionById,
  removeQuestionRule,
  toggleOptionEnabled,
  updateOption,
  upsertQuestionRule,
} from 'rtk/Question.Editor.Slice';
import { applyAutomaticKnockout } from 'rtk/TemplateSlice';
import {
  selectOptionReferencesVisible,
  selectQuestionIsPublishedOrNonExclusive,
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionOptionIndexById,
} from 'rtk/questionEditorSelector';
import { updateInTemplateOptionEvaluation, updateInTemplateOptionEnabled } from 'rtk/questionEditorThunks';
import { EEventType } from 'types/events.enum';
import ClickToCopyButton from 'components/common/ClickToCopyButton';
import { useInRecruiterApp } from 'hooks/useInRecruiterApp';
import { useUser } from 'hooks/useUser';
import { isDeveloper, isHubertAdminUser } from 'types/user.types';

const CreateOption = ({
  data,
  evaluation,
  currentRules,
  inTemplateEditAndUneditable,
}: {
  data: {
    item: TSingleSelectOption;
    index: number;
  };
  // Question-level, hoisted to the list's parent: calling these hooks per row means one live
  // subscription (and one whole-question evaluation) per visible option.
  evaluation: ReturnType<typeof useQuestionEvaluation>['evaluation'];
  currentRules: TQuestionRule[];
  inTemplateEditAndUneditable: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const referencesVisible = useAppSelector((state) => selectOptionReferencesVisible(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const showIfNotInRecruiter = !useInRecruiterApp();
  const { t } = useTranslation('Questionsv2');
  const user = useUser();

  const isUneditable = useAppSelector((state) => selectQuestionIsPublishedOrNonExclusive(state, questionId));
  const templateId = useTemplateId();

  const [label, setLabel] = useState(data.item.label[questionLanguage] || '');
  const [reference, setReference] = useState(data.item.reference ?? '');
  const optionIndex = useAppSelector((state) => selectQuestionOptionIndexById(state, questionId, data.item.id));
  const fieldIndex = optionIndex >= 0 ? optionIndex : data.index;

  useEffect(() => {
    setLabel(data.item.label[questionLanguage] || '');
  }, [questionLanguage, data.item.label]);

  useEffect(() => {
    setReference(data.item.reference ?? '');
  }, [data.item.reference]);

  const updateField = (field: 'label' | 'reference', setLocal: (value: string) => void, value: string) => {
    setLocal(value);
    if (optionIndex < 0) return;
    dispatch(
      updateOption({
        questionId,
        language: questionLanguage,
        index: optionIndex,
        field,
        value: field === 'reference' ? value || undefined : value,
      }),
    );
  };

  return (
    <div className="hover:bg-neutral-purple flex w-full items-center justify-between rounded-md pt-2 pr-3 pb-2 pl-3">
      <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
        <div className="flex w-full min-w-0 flex-col">
          {showIfNotInRecruiter ? (
            <ClickToCopyButton
              text={t('selects.optionId', { id: data.item.id })}
              copyText={String(data.item.id)}
              className="my-0.5 py-0 text-sm"
            />
          ) : null}
          <div className="flex w-full min-w-0 flex-row gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {type === EQuestionType.SingleSelect ? (
                <MutedText text={t('selects.optionLabelCaption')} className="text-xs" />
              ) : null}
              <HubertInput
                placeholder={t('selects.optionPlaceholder')}
                tabIndex={0}
                disabled={inTemplateEditAndUneditable}
                type="text"
                dir={'auto'}
                value={label}
                onChange={(e) => updateField('label', setLabel, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.shiftKey) {
                    e.preventDefault();
                    dispatch(addOption({ questionId, language: questionLanguage }));
                    return;
                  }
                }}
                className="w-full"
              />
            </div>
            {type === EQuestionType.SingleSelect && referencesVisible && isHubertAdminUser(user) ? (
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <MutedText text={t('selects.optionReferenceCaption')} className="text-xs" />
                <HubertInput
                  placeholder={label || t('selects.optionPlaceholder')}
                  tabIndex={0}
                  disabled={inTemplateEditAndUneditable}
                  type="text"
                  dir={'auto'}
                  value={reference}
                  onChange={(e) => updateField('reference', setReference, e.target.value)}
                  className="w-full"
                />
              </div>
            ) : null}
          </div>
        </div>
      </UneditableInfoTooltip>

      <div className={`${showIfNotInRecruiter ? 'mt-5' : ''} flex flex-row items-center justify-end gap-2`}>
        <EvaluationPopover
          allowedTabs={
            type === EQuestionType.SingleSelect
              ? ['info', 'warning', 'bonus', 'disqualified', 'auto_qualified']
              : ['info', 'warning', 'bonus', 'disqualified']
          }
          nodeId={questionId}
          customKey={`${data.item.id}`}
          templateId={mode === ECreateQuestionMode.InTemplateEdit ? templateId : undefined}
          evaluation={
            // Outside a template, evaluation changes are held in local editor state until
            // "Update question" is pressed (see onEvaluationChange below) — so the option's own
            // live evaluation/bonus_points must be shown directly. The template-evaluation-aware
            // summary below is sourced from the Library cache, which only refreshes on a server
            // round trip, and is only kept fresh immediately in InTemplateEdit mode (where every
            // evaluation change persists right away).
            mode === ECreateQuestionMode.InTemplateEdit &&
            evaluation &&
            hasNestedKeys(evaluation, 'evaluation', `${data.item.id}`)
              ? // @ts-ignore
                evaluation.evaluation[`${data.item.id}`]
              : {
                  name: data.item.evaluation,
                  bonus_points: data.item.bonus_points,
                }
          }
          isDefault
          onEvaluationChange={(e, earlyReject?: EarlyReject, removeEarlyRejectRuleId?: string) => {
            if (optionIndex < 0) return;
            dispatch(
              updateOption({
                questionId,
                language: questionLanguage,
                index: optionIndex,
                field: 'evaluation',
                value: e.name,
                bonusPoints: e.bonus_points ?? undefined,
              }),
            );
            if (mode === ECreateQuestionMode.InTemplateEdit) {
              const optionId = data.item.id;
              if (earlyReject) {
                dispatch(
                  applyAutomaticKnockout({
                    templateId,
                    eventType: EEventType.ADDED,
                    resource: {
                      nodeId: questionId,
                      optionId,
                      rule: earlyReject.rule,
                      currentRules,
                      addQuestionIds: earlyReject.addQuestionIds,
                    },
                    description: `User created an early reject sequence for ${questionId}/${optionId} via ${earlyReject.addQuestionIds.join(', ')}.`,
                  }),
                );
              } else {
                updateInTemplateOptionEvaluation(
                  templateId,
                  optionId,
                  questionId,
                  type,
                  dispatch,
                  mode,
                  e,
                  removeEarlyRejectRuleId ? [removeEarlyRejectRuleId] : undefined,
                  currentRules,
                );
              }
            } else if (earlyReject) {
              dispatch(upsertQuestionRule({ questionId, rule: earlyReject.rule }));
            } else if (removeEarlyRejectRuleId) {
              dispatch(removeQuestionRule({ questionId, ruleId: removeEarlyRejectRuleId }));
            }
          }}
          answerType={'option'}
        />

        <ReplyPopover
          replies={data.item.replies?.[questionLanguage] ?? []}
          field="options"
          fieldIndex={fieldIndex}
          optionId={data.item.id}
        />

        {isDeveloper(user) ? <SelectOptionRolePopover role={data.item.role} optionIndex={optionIndex} /> : null}

        {data.item.actions ? (
          <OptionActionPopover actions={data.item.actions?.[questionLanguage] ?? []} fieldIndex={fieldIndex} />
        ) : null}

        <HubertSwitch
          disabled={isReadOnly}
          checked={data.item.enabled[questionLanguage]}
          onCheckedChange={(value) => {
            if (optionIndex < 0) return;
            dispatch(toggleOptionEnabled({ questionId, language: questionLanguage, index: optionIndex, value }));
            const optionId = data.item.id;
            updateInTemplateOptionEnabled(templateId, optionId, questionId, questionLanguage, dispatch, mode, value);
          }}
        />

        <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
          <IconButton
            variant="ghost"
            color="red"
            className={isUneditable ? '' : 'cursor-pointer!'}
            disabled={isUneditable}
            onClick={() => dispatch(removeOptionById({ questionId, id: data.item.id }))}
          >
            <Trash2Icon />
          </IconButton>
        </UneditableInfoTooltip>
      </div>
    </div>
  );
};

export default CreateOption;
